import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { getUsage } from '@/api/usage';

const RANGES = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 }
] as const;

export function UsageScreen() {
  const [range, setRange] = useState<(typeof RANGES)[number]>(RANGES[1]);
  const { data, isLoading } = useQuery({
    queryKey: ['usage', range.days],
    queryFn: () => getUsage(range.days)
  });

  return (
    <div className="space-y-8">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-nurev-text">Usage</h1>
          <p className="mt-1 text-sm text-nurev-muted">
            Request volume, token throughput, and spend across your account.
          </p>
        </div>
        <div className="flex gap-1 rounded-lg border border-nurev-border bg-nurev-surface p-1">
          {RANGES.map(r => (
            <button
              key={r.label}
              onClick={() => setRange(r)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                r.label === range.label
                  ? 'bg-brand-gradient text-white'
                  : 'text-nurev-text-soft hover:text-nurev-text'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      {isLoading || !data ? (
        <div className="flex items-center gap-3 text-nurev-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading usage…
        </div>
      ) : (
        <>
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Stat
              label="Requests"
              value={data.totals.totalRequests.toLocaleString()}
            />
            <Stat
              label="Tokens"
              value={data.totals.totalTokens.toLocaleString()}
            />
            <Stat
              label="Spend"
              value={`$${(data.totals.totalPriceMicrocents / 1_000_000).toFixed(4)}`}
            />
          </section>

          <section className="card">
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-nurev-purple-light">
              Daily volume
            </h2>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={data.byDay.map(d => ({
                    date: new Date(d.date).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric'
                    }),
                    requests: d.requests,
                    tokens: d.tokens
                  }))}
                  margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="g-req" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#a78bfa" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    stroke="rgba(108, 79, 212, 0.12)"
                    strokeDasharray="3 3"
                  />
                  <XAxis
                    dataKey="date"
                    stroke="#7b7ea8"
                    fontSize={11}
                    tickLine={false}
                  />
                  <YAxis stroke="#7b7ea8" fontSize={11} tickLine={false} />
                  <Tooltip
                    contentStyle={{
                      background: '#15172b',
                      border: '1px solid rgba(108, 79, 212, 0.35)',
                      borderRadius: 8,
                      color: '#eeeeff',
                      fontSize: 12
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="requests"
                    stroke="#a78bfa"
                    strokeWidth={2}
                    fill="url(#g-req)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Breakdown title="By model" rows={data.byModel} />
            <Breakdown title="By endpoint" rows={data.byEndpoint} />
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card-tight">
      <div className="label">{label}</div>
      <div className="text-2xl font-bold text-nurev-text">{value}</div>
    </div>
  );
}

function Breakdown({
  title,
  rows
}: {
  title: string;
  rows: { _id: string; requests: number; tokens: number }[];
}) {
  return (
    <div className="card">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-nurev-purple-light">
        {title}
      </h2>
      {rows.length === 0 ? (
        <p className="text-sm text-nurev-muted">No activity in this window.</p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase tracking-wide text-nurev-muted">
            <tr>
              <th className="pb-2 font-semibold">Name</th>
              <th className="pb-2 text-right font-semibold">Requests</th>
              <th className="pb-2 text-right font-semibold">Tokens</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r._id || '(none)'} className="border-t border-nurev-border">
                <td className="py-2">
                  <code className="code-pill">{r._id || '—'}</code>
                </td>
                <td className="py-2 text-right text-nurev-text">
                  {r.requests.toLocaleString()}
                </td>
                <td className="py-2 text-right text-nurev-text">
                  {r.tokens.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
